// Dashboard heads-up aggregations. The health metrics come from the precomputed
// `districtSummaries` docs (one per district, maintained by the
// recomputeDistrictSummaries Cloud Function) — the Dashboard never scans
// unitLeaders / unitSyt / unitAdvancementSnapshots. Committee health still reads
// `contacts` (the roster) and the membership trend reads `membershipSnapshots`,
// both bounded.
//
// KEEP IN SYNC with functions/src/districtSummaries.ts (the server computes the
// same numbers) and src/lib/unitHealth.ts (shared tone helpers).

import type {
  AffiliatedHolder,
  Committee,
  Contact,
  DashboardThresholds,
  DistrictSummary,
  MembershipSnapshot,
  NamedRecord,
  Position,
} from '../types';
import { toneFor, type Tone } from './unitHealth.ts';
import { filledByAffiliation } from './affiliatedHolders.ts';

const isVacant = (name: string) => !name || /vacant/i.test(name);
const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);
const inScope = (districtId: string | null, id: string | null | undefined) =>
  districtId == null || id === districtId;

// ---- District summary access -----------------------------------

const EMPTY_SUMMARY: DistrictSummary = {
  id: '',
  districtId: '',
  tenantId: '',
  recharter: { chartered: 0, soon: 0, overdue: 0, unknown: 0 },
  leadershipGaps: 0,
  syt: { current: 0, total: 0 },
  trained: { current: 0, total: 0 },
  weather: { current: 0, total: 0 },
  advancementYtd: { eagle: 0, scoutsBsaRanks: 0, cubRanks: 0 },
  advancementPace: { onTrack: 0, behind: 0, stalled: 0, notRated: 0 },
  membershipLatest: null,
};

/** Sum the numeric fields of several district summaries — the council rollup. */
export function rollupSummaries(summaries: DistrictSummary[]): DistrictSummary {
  return summaries.reduce<DistrictSummary>(
    (acc, s) => ({
      ...acc,
      recharter: {
        chartered: acc.recharter.chartered + s.recharter.chartered,
        soon: acc.recharter.soon + s.recharter.soon,
        overdue: acc.recharter.overdue + s.recharter.overdue,
        unknown: acc.recharter.unknown + s.recharter.unknown,
      },
      leadershipGaps: acc.leadershipGaps + s.leadershipGaps,
      syt: { current: acc.syt.current + s.syt.current, total: acc.syt.total + s.syt.total },
      trained: {
        current: acc.trained.current + s.trained.current,
        total: acc.trained.total + s.trained.total,
      },
      weather: {
        current: acc.weather.current + s.weather.current,
        total: acc.weather.total + s.weather.total,
      },
      advancementYtd: {
        eagle: acc.advancementYtd.eagle + s.advancementYtd.eagle,
        scoutsBsaRanks: acc.advancementYtd.scoutsBsaRanks + s.advancementYtd.scoutsBsaRanks,
        cubRanks: acc.advancementYtd.cubRanks + s.advancementYtd.cubRanks,
      },
      advancementPace: {
        onTrack: acc.advancementPace.onTrack + s.advancementPace.onTrack,
        behind: acc.advancementPace.behind + s.advancementPace.behind,
        stalled: acc.advancementPace.stalled + s.advancementPace.stalled,
        notRated: acc.advancementPace.notRated + s.advancementPace.notRated,
      },
    }),
    { ...EMPTY_SUMMARY },
  );
}

/** The summary for one district, or the council rollup when `districtId` is null. */
export function scopeSummary(
  summaries: DistrictSummary[],
  districtId: string | null,
): DistrictSummary {
  if (districtId == null) return rollupSummaries(summaries);
  return summaries.find((s) => s.districtId === districtId) ?? { ...EMPTY_SUMMARY, districtId };
}

// ---- Charter renewal -------------------------------------------------

export interface CharterCounts {
  chartered: number;
  soon: number;
  overdue: number;
  unknown: number;
}

export function charterCounts(
  summaries: DistrictSummary[],
  districtId: string | null,
): CharterCounts {
  return { ...scopeSummary(summaries, districtId).recharter };
}

export interface CharterBar extends CharterCounts {
  districtId: string;
  name: string;
}

export function charterByDistrict(
  summaries: DistrictSummary[],
  districts: NamedRecord[],
): CharterBar[] {
  return districts.map((d) => ({
    districtId: d.id,
    name: d.name,
    ...scopeSummary(summaries, d.id).recharter,
  }));
}

// ---- SYT / Trained ----------------------------------------------

export interface Ratio {
  done: number;
  total: number;
  pct: number;
}

export function sytStats(summaries: DistrictSummary[], districtId: string | null): Ratio {
  const { current, total } = scopeSummary(summaries, districtId).syt;
  return { done: current, total, pct: pct(current, total) };
}

export function trainedStats(summaries: DistrictSummary[], districtId: string | null): Ratio {
  const { current, total } = scopeSummary(summaries, districtId).trained;
  return { done: current, total, pct: pct(current, total) };
}

/** Hazardous Weather Training "current" ratio for the Leader Readiness
 *  donut — `total` is ONLY leaders for whom it's applicable (see
 *  DistrictSummary.weather's own doc comment); `done` counts CURRENT +
 *  DUE_SOON. total === 0 renders as the Donut component's existing
 *  no-data state ("—"), never a misleading 0%. */
export function weatherStats(summaries: DistrictSummary[], districtId: string | null): Ratio {
  const { current, total } = scopeSummary(summaries, districtId).weather;
  return { done: current, total, pct: pct(current, total) };
}

// ---- Advancement -----------------------------------------------

export interface AdvancementYtd {
  eagleYtd: number;
  scoutsBsaRanksYtd: number;
  cubRanksYtd: number;
}

export function advancementYtd(
  summaries: DistrictSummary[],
  districtId: string | null,
): AdvancementYtd {
  const a = scopeSummary(summaries, districtId).advancementYtd;
  return { eagleYtd: a.eagle, scoutsBsaRanksYtd: a.scoutsBsaRanks, cubRanksYtd: a.cubRanks };
}

export interface AdvancementPace {
  onTrack: number;
  behind: number;
  stalled: number;
  notRated: number;
}

export function advancementPace(
  summaries: DistrictSummary[],
  districtId: string | null,
): AdvancementPace {
  return { ...scopeSummary(summaries, districtId).advancementPace };
}

export interface AdvancementPaceBar extends AdvancementPace {
  districtId: string;
  name: string;
}

export function advancementPaceByDistrict(
  summaries: DistrictSummary[],
  districts: NamedRecord[],
): AdvancementPaceBar[] {
  return districts.map((d) => ({
    districtId: d.id,
    name: d.name,
    ...scopeSummary(summaries, d.id).advancementPace,
  }));
}

// ---- Committee health ------------------------------------------
// A "seat" is one (committee position × district). Filled = at least one
// non-vacant roster contact holds it in that district.

export interface CommitteeFill {
  key: string;
  committeeName: string;
  districtId: string;
  districtName: string;
  filled: number;
  total: number;
  pct: number;
}

export interface CommitteeHealth {
  filled: number;
  total: number;
  pct: number;
  flagged: CommitteeFill[];
}

export function committeeHealth(
  contacts: Contact[],
  positions: Position[],
  committees: Committee[],
  districts: NamedRecord[],
  districtId: string | null,
  flagPct: number,
  /** People who hold a district seat from outside the district (District Affiliations): they fill a Position that is defined
   *  already and never add one. Absent = exactly the roster-only result. */
  affiliated: readonly AffiliatedHolder[] = [],
): CommitteeHealth {
  const scopeDistricts = districtId ? districts.filter((d) => d.id === districtId) : districts;
  const committeePositions = positions.filter((p) => p.committee);
  const committeeName = (id: string) => committees.find((c) => c.id === id)?.name ?? id;

  const holdersByDistrict = new Map<string, Set<string>>(); // districtId -> position names with a real holder
  for (const c of contacts) {
    if (isVacant(c.name) || !c.districtId) continue; // a council-level Contact belongs to no district's committee
    const set = holdersByDistrict.get(c.districtId) ?? new Set<string>();
    for (const p of c.positions) set.add(p);
    holdersByDistrict.set(c.districtId, set);
  }

  const filledFromOutside = filledByAffiliation(affiliated); // districtId -> Position ids

  const perCommittee: CommitteeFill[] = [];
  let filled = 0;
  let total = 0;
  for (const d of scopeDistricts) {
    const held = holdersByDistrict.get(d.id) ?? new Set<string>();
    const heldFromOutside = filledFromOutside.get(d.id);
    const byCommittee = new Map<string, { filled: number; total: number }>();
    for (const p of committeePositions) {
      const agg = byCommittee.get(p.committee) ?? { filled: 0, total: 0 };
      agg.total++;
      if (held.has(p.name) || heldFromOutside?.has(p.positionId)) agg.filled++;
      byCommittee.set(p.committee, agg);
    }
    for (const [cid, agg] of byCommittee) {
      filled += agg.filled;
      total += agg.total;
      perCommittee.push({
        key: `${d.id}:${cid}`,
        committeeName: committeeName(cid),
        districtId: d.id,
        districtName: d.name,
        filled: agg.filled,
        total: agg.total,
        pct: pct(agg.filled, agg.total),
      });
    }
  }

  const flagged = perCommittee
    .filter((c) => c.total > 0 && c.pct <= flagPct)
    .sort((a, b) => a.pct - b.pct || a.districtName.localeCompare(b.districtName));

  return { filled, total, pct: pct(filled, total), flagged };
}

// ---- Membership trend ------------------------------------------

export interface MembershipPoint {
  month: string;
  youth: number;
  adults: number;
  units: number;
}

export function membershipSeries(
  snaps: MembershipSnapshot[],
  districtId: string | null,
): MembershipPoint[] {
  const byMonth = new Map<string, MembershipPoint>();
  for (const s of snaps) {
    if (!inScope(districtId, s.districtId)) continue;
    const p = byMonth.get(s.month) ?? { month: s.month, youth: 0, adults: 0, units: 0 };
    p.youth += s.youth || 0;
    p.adults += s.adults || 0;
    p.units += s.units || 0;
    byMonth.set(s.month, p);
  }
  return [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
}

// ---- Alert strip --------------------------------------------------

export interface AlertCounts {
  recharterOverdue: number;
  recharterSoon: number;
  leadershipGaps: number;
  sytFlags: number;
  /** MISSING + EXPIRED applicable leaders — same "total minus compliant"
   *  shape as sytFlags. DUE_SOON is compliant (counted in weather.current),
   *  so it's never part of this — it's proactive information, not a flag.
   *  N/A leaders were never in weather.total to begin with. */
  weatherFlags: number;
}

export function alertCounts(
  summaries: DistrictSummary[],
  districtId: string | null,
): AlertCounts {
  const s = scopeSummary(summaries, districtId);
  return {
    recharterOverdue: s.recharter.overdue,
    recharterSoon: s.recharter.soon,
    leadershipGaps: s.leadershipGaps,
    sytFlags: s.syt.total - s.syt.current,
    weatherFlags: s.weather.total - s.weather.current,
  };
}

// ---- District comparison heatmap ---------------------------------

export interface HeatCell {
  value: number; // percentage, or a raw count for `stalled`
  display: string;
  tone: Tone;
}

export interface HeatRow {
  districtId: string;
  name: string;
  recharter: HeatCell;
  syt: HeatCell;
  trained: HeatCell;
  stalled: HeatCell;
}

export function heatmapRows(
  summaries: DistrictSummary[],
  districts: NamedRecord[],
  t: DashboardThresholds,
): HeatRow[] {
  return districts.map((d) => {
    const s = scopeSummary(summaries, d.id);
    const cc = s.recharter;
    const charteredTotal = cc.chartered + cc.soon + cc.overdue;
    const recharterPct = pct(cc.chartered, charteredTotal);
    const sy = { pct: pct(s.syt.current, s.syt.total), total: s.syt.total };
    const tr = { pct: pct(s.trained.current, s.trained.total), total: s.trained.total };
    const pace = s.advancementPace;
    return {
      districtId: d.id,
      name: d.name,
      recharter: {
        value: recharterPct,
        display: charteredTotal ? `${recharterPct}%` : '—',
        tone: toneFor(recharterPct, t.recharterOkPct, t.recharterWarnPct),
      },
      syt: {
        value: sy.pct,
        display: sy.total ? `${sy.pct}%` : '—',
        tone: toneFor(sy.pct, t.sytOkPct, t.sytWarnPct),
      },
      trained: {
        value: tr.pct,
        display: tr.total ? `${tr.pct}%` : '—',
        tone: toneFor(tr.pct, t.trainedOkPct, t.trainedWarnPct),
      },
      stalled: {
        value: pace.stalled,
        display: String(pace.stalled),
        tone: pace.stalled === 0 ? 'ok' : pace.stalled <= 2 ? 'amber' : 'red',
      },
    };
  });
}
